import {useState} from "react";
import {Box, Collapse, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {appStyles} from "../../../../AppStyles.ts";

// Report section data model — mirrors desktop ProfileSummary structure
interface ReportSection {
    title: string;
    paragraphs: string[];
    subsections?: ReportSubsection[];
}

interface ReportSubsection {
    heading: string;
    paragraphs: string[];
}

// TODO: Replace with API call to load summary report from IntelProfileManager
function loadSampleReport(): ReportSection[] {
    return [
        {
            title: "Profile Summary",
            paragraphs: [
                "This intelligence profile aggregates observations and identifying information collected from multiple sources over the operational period. The subject has been associated with several persons of interest and appears in records spanning more than three distinct geographic regions.",
                "Initial enrichment was performed against the standard registry indices and subsequent matches were resolved through name, address, and temporal resolvers. Confidence scores remain within acceptable thresholds for analyst review.",
            ],
            subsections: [
                {
                    heading: "Key Observations",
                    paragraphs: [
                        "Multiple high-confidence resolver matches were recorded against the primary alias, with corroborating data points from at least two independent sources.",
                        "Temporal patterns suggest consistent activity within a recurring window, primarily during weekday business hours in the local time zone of the resolved address.",
                    ],
                },
                {
                    heading: "Outstanding Items",
                    paragraphs: [
                        "Several unresolved hash matches remain pending review by the analyst team. These items have been flagged but not yet incorporated into the active profile.",
                    ],
                },
            ],
        },
        {
            title: "Background",
            paragraphs: [
                "Background information has been compiled from publicly available sources, partner network feeds, and internal historical records. Where applicable, source provenance has been linked to the underlying records and is available through the Provenance panel.",
                "No conflicting biographical claims have been identified in the current dataset. All listed identifiers have been cross-referenced against the active intelligence definitions for this profile type.",
            ],
        },
        {
            title: "Analyst Notes",
            paragraphs: [
                "Analyst notes from the most recent review cycle indicate that this profile is considered active and warrants continued monitoring. The next scheduled review is dependent on additional enrichment results and any new resolver matches that surface during the interim.",
            ],
        },
    ];
}

function UXIntelSummaryPanel() {

    const report = loadSampleReport();
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set(report.map((_, i) => i)));

    const allExpanded = expanded.size === report.length;

    const toggleSection = (idx: number) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const toggleAll = () => {
        if (allExpanded) setExpanded(new Set());
        else setExpanded(new Set(report.map((_, i) => i)));
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            <Stack direction="row" padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={allExpanded ? "Collapse All" : "Expand All"}>
                        <IconButton size="small" sx={appStyles.panelIconButton} onClick={toggleAll}>
                            {allExpanded
                                ? <UnfoldLessIcon sx={{fontSize: 18, color: "#F2F5FA"}} />
                                : <UnfoldMoreIcon sx={{fontSize: 18, color: "#F2F5FA"}} />}
                        </IconButton>
                    </Tooltip>
                    <Typography sx={appStyles.panelSectionTitle}>Summary Report</Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{position: "absolute", right: 8}}>
                    <Tooltip title="Copy to Clipboard">
                        <IconButton size="small" sx={appStyles.panelIconButton}>
                            <ContentCopyIcon sx={{fontSize: 16, color: "#F2F5FA"}} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Print">
                        <IconButton size="small" sx={appStyles.panelIconButton}>
                            <PrintIcon sx={{fontSize: 16, color: "#F2F5FA"}} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export">
                        <IconButton size="small" sx={appStyles.panelIconButton}>
                            <FileDownloadIcon sx={{fontSize: 16, color: "#F2F5FA"}} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
            <Box sx={{
                flex: 1,
                overflow: "auto",
                px: 4,
                py: 3,
                mx: 2,
                mb: 2,
                mt: 1,
            }}>
                <Stack direction="column" spacing={4} sx={{maxWidth: 900, mx: "auto"}}>
                    {report.map((section, idx) => {
                        const isOpen = expanded.has(idx);
                        return (
                            <Stack key={idx} direction="column" spacing={1.5}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    onClick={() => toggleSection(idx)}
                                    sx={{
                                        borderBottom: "1px solid #a8b8c0",
                                        pb: 1,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        "&:hover": {opacity: 0.85},
                                    }}
                                >
                                    {isOpen
                                        ? <ExpandMoreIcon sx={{fontSize: 22, color: "#a8b8c0"}} />
                                        : <ChevronRightIcon sx={{fontSize: 22, color: "#a8b8c0"}} />}
                                    <Box sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #02cfee",
                                    }} />
                                    <Typography sx={{
                                        color: "#a8b8c0",
                                        fontSize: 18,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                    }}>
                                        {section.title}
                                    </Typography>
                                </Stack>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Stack direction="column" spacing={1.5}>
                                        {section.paragraphs.map((para, pIdx) => (
                                            <Typography key={pIdx} sx={{
                                                color: "#FFFFFF",
                                                fontSize: 13,
                                                fontWeight: 300,
                                                lineHeight: 1.7,
                                                textAlign: "justify",
                                            }}>
                                                {para}
                                            </Typography>
                                        ))}
                                        {section.subsections?.map((sub, sIdx) => (
                                            <Stack key={sIdx} direction="column" spacing={1} sx={{mt: 3}}>
                                                <Typography sx={{
                                                    color: "#FFFFFF",
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    textAlign: "left",
                                                    mb: 0.5,
                                                }}>
                                                    {sub.heading}
                                                </Typography>
                                                {sub.paragraphs.map((para, pIdx) => (
                                                    <Typography key={pIdx} sx={{
                                                        color: "#FFFFFF",
                                                        fontSize: 13,
                                                        fontWeight: 300,
                                                        lineHeight: 1.7,
                                                        textAlign: "justify",
                                                    }}>
                                                        {para}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Collapse>
                            </Stack>
                        );
                    })}
                </Stack>
            </Box>
        </Stack>
    );
}

export default UXIntelSummaryPanel;
